import type { QuillRendererRef, TableMenuItem } from '@/components_deprecated'
import {
  Button,
  DropArea,
  HandbookEntryAttachmentsContainer,
  QuillRenderer,
} from '@/components_deprecated'
import { PopupMenu } from '@/components_deprecated/PopupMenu'
import { FormForm } from '@/modules/formcreator/components'
import { PostAttachment } from '@/modules/forum/components/PostAttachment'
import { ZIndexRange } from '@/types/style'
import { format } from '@/utils/date-fns'
import { quillValidOrHasAttachments } from '@/utils/forms'
import {
  useAttachmentHandlers,
  useConfirm,
  useParam,
  usePermissions,
} from '@/utils/hooks'
import { safeParseJson } from '@/utils/parsing'
import { PERMISSIONS } from '@/utils/permissions'
import type { Image } from '@ur-solutions/react-components'
import { Icon, ImageViewer } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type Quill from 'quill'
import Delta from 'quill-delta'
import type Op from 'quill-delta/dist/Op'
import React, { useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useLocation } from 'react-router'
import styled, { useTheme } from 'styled-components'
import type { EditHandbookEntryForm, EntryQuillEditorRef } from '../components'
import {
  AddAttachment,
  ContentWrapper,
  Description,
  DescriptionInput,
  EditButtons,
  EntryQuillEditor,
  SelectHandbookForm,
  TitleInput,
} from '../components'
import { HANDBOOK_NAME_MAX_LENGTH, ManualEntryPlaceholder } from '../consts'
import { useHandbookEntryMutations, useHandbookEntryQuery } from '../hooks'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  & > header {
    margin-bottom: 1rem;
    padding-left: 4px;

    h2 {
      margin: 0;
    }
    p {
      margin: 6px 0 0 0;
      font-weight: 500;
      color: ${props => props.theme.colors.gray5};
    }
  }
`
interface RichtextProps {
  editing: boolean
}
const Richtext = styled.article<RichtextProps>`
  overflow-x: clip;
  ${props => props.editing && 'padding: 0 !important;'};
`

export const HandbookEntry: React.FC = () => {
  const translations = useTranslate({
    edit: 'common.edit',
    delete: 'common.delete-alt',
    cancel: 'common.cancel',
    save: 'common.save',
    published: 'common.published',
    lastRevision: 'common.last-revision',

    validation: {
      required: 'common.required',
    },
    prompt: {
      deleteEntry: 'handbook.prompts.delete-entry',
      deleteEntryTitle: 'handbook.prompts.delete-entry-title',
    },
  })

  const folderSlug = useParam('folderSlug')
  const entrySlug = useParam('entrySlug')

  const theme = useTheme()

  const quillRendererRef = useRef<QuillRendererRef>(null)
  const quillEditorRef = useRef<EntryQuillEditorRef>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const location = useLocation()
  const confirm = useConfirm()
  const { hasPermissions } = usePermissions()

  const [quillText, setQuillText] = useState('')
  const [editing, setEditing] = useState(!!location.state?.edit)
  const [imageViewerInitialImage, setImageViewerInitialImage] = useState('')
  const [imageViewerAttachments, setImageViewerAttachments] = useState<Image[]>(
    []
  )

  const {
    formValues: editForm,
    formErrors: editErrors,
    formValid: editValid,
    formChangeHandler: handler,
    updateForm,
    resetForm,
    updateInitialValues,
    submitHandler: submit,
  } = useForm<EditHandbookEntryForm>({
    values: {
      name: '',
      description: '',
      richtext: '{}',
      form: null,
    },
    validators: {
      name: val => (val ? null : translations.validation.required),
      richtext: () =>
        handlequillValidOrHasAttachments(translations.validation.required),
    },
  })

  const { data, loading: queryLoading } = useHandbookEntryQuery(
    entrySlug,
    folderSlug,
    updateForm,
    updateInitialValues,
    data => {
      if (editing && !!quillEditorRef.current?.quill) {
        const delta = new Delta(
          safeParseJson<Op[]>(data.manualEntry.richtext, [])
        )
        quillEditorRef.current.quill.setContents(delta)
        setQuillText(quillEditorRef.current.quill.getText())
      }
    }
  )

  const entry = data?.manualEntry ?? ManualEntryPlaceholder

  const {
    allAttachments,
    attachmentsAdd,
    attachmentsRemove,
    resetEditedAttachments,
    handleDropAttachment,
    handleAddAttachment,
    handleRemoveAttachment,
    handleAttachmentClick,
  } = useAttachmentHandlers(
    entry.attachments,
    setImageViewerInitialImage,
    setImageViewerAttachments
  )

  const isFormValid = useMemo(
    () => editValid || (!!editForm.name && !!allAttachments.length),
    [allAttachments.length, editValid, editForm.name]
  )
  /**
   * Convenience function to check if the current Quill and Attachments state is valid.
   * @param translation - A valid translation string
   */
  function handlequillValidOrHasAttachments(translation: string) {
    const delta = quillEditorRef.current?.quill?.getContents()
    return quillValidOrHasAttachments(delta, allAttachments)
      ? null
      : translation
  }

  const mutations = useHandbookEntryMutations(
    entrySlug,
    folderSlug,
    editForm,
    updateForm,
    updateInitialValues,
    setEditing,
    resetEditedAttachments
  )

  const form =
    !entry || entry.form?.archived || !entry.form?.formFields.edges.length
      ? null
      : entry.form

  function handleCancel() {
    setEditing(false)
    resetForm()
  }

  async function handleSave(values: EditHandbookEntryForm) {
    if (!entry.id) return

    await mutations.patch({
      variables: {
        id: entry.id,
        input: {
          ...values,
          attachmentsAdd: attachmentsAdd.map(attachment => ({
            name: attachment.name,
            file: attachment.file,
          })),
          attachmentsRemove,
          plaintext: quillText,
        },
      },
    })
  }
  async function handleDelete() {
    const { data: answer } = await confirm(
      translations.prompt.deleteEntry,
      translations.prompt.deleteEntryTitle
    )
    if (!answer) return

    void mutations.delete({
      variables: { id: entry.id },
    })
  }

  function handleInitQuillEditor(quill: Quill) {
    const delta = new Delta(safeParseJson<Op[]>(editForm.richtext, []))
    quill.setContents(delta)
    setQuillText(quill.getText())
  }

  const menuItems: TableMenuItem<never>[] = [
    {
      label: translations.edit,
      hide: !hasPermissions(PERMISSIONS.handbook.change.manualentry, true),
      onClick: () => setEditing(true),
    },
    {
      label: translations.delete,
      hide: !hasPermissions(PERMISSIONS.handbook.delete.manualentry, true),
      onClick: handleDelete,
    },
  ]

  const isLoading = queryLoading || mutations.loading
  return (
    <Wrapper>
      <ImageViewer
        images={imageViewerAttachments}
        zIndex={ZIndexRange.Extra}
        initialImage={imageViewerInitialImage}
        popup
        open={imageViewerAttachments.length > 0}
        noShadow
        showArrows
        background="black !important"
        imageSize="contain"
        imageBackgroundColor="black !important"
        onClose={() => setImageViewerAttachments([])}
      />

      <header>
        {editing ? (
          <TitleInput
            value={editForm.name}
            error={editErrors.name}
            autoFocus
            fullWidth
            maxLength={HANDBOOK_NAME_MAX_LENGTH}
            fontWeight={600}
            onChange={handler('name')}
          />
        ) : (
          <h2>{entry.name}</h2>
        )}
        <p>
          {translations.published}: {format(new Date(entry.createdAt), 'PPP')}
        </p>
        <p>
          {translations.lastRevision}:{' '}
          {format(new Date(entry.updatedAt), 'PPP')}
        </p>
      </header>

      {!!form && !editing && <FormForm form={form} collapsible />}
      {editing && (
        <SelectHandbookForm value={editForm.form} onChange={handler('form')} />
      )}

      <ContentWrapper>
        <Description>
          {editing ? (
            <DescriptionInput
              value={editForm.description}
              onChange={handler('description')}
            />
          ) : (
            <p>{entry.description}</p>
          )}

          {!!menuItems.length && (
            <>
              {editing ? (
                <EditButtons>
                  <Button background="gray8" onClick={handleCancel}>
                    {isMobile ? <Icon icon="times" /> : translations.cancel}
                  </Button>
                  <Button disabled={!isFormValid} onClick={submit(handleSave)}>
                    {isMobile ? <Icon icon="save" /> : translations.save}
                  </Button>
                </EditButtons>
              ) : (
                <PopupMenu items={menuItems} loading={isLoading} />
              )}
            </>
          )}
        </Description>

        {((entry.richtext && entry.richtext !== '{}') || editing) && (
          <Richtext
            editing={editing}
            onClick={() => editing && quillEditorRef.current?.quill?.focus()}
          >
            {editing ? (
              <EntryQuillEditor
                ref={quillEditorRef}
                hasImage={true}
                onInit={handleInitQuillEditor}
                onChange={(content, quill) => {
                  handler('richtext')(content)
                  setQuillText(quill.getText())
                }}
              />
            ) : (
              <QuillRenderer
                ref={quillRendererRef}
                contents={safeParseJson(entry.richtext)}
              />
            )}
          </Richtext>
        )}

        {(allAttachments.length > 0 || editing) && (
          <DropArea
            disabled={!editing || isMobile}
            dragStyle={{
              backgroundColor: theme.colors.primary,
            }}
            onDrop={handleDropAttachment}
          >
            <HandbookEntryAttachmentsContainer>
              {allAttachments.map(attachment => (
                <PostAttachment
                  key={attachment.id}
                  attachment={attachment}
                  deletable={editing}
                  height="100px"
                  onDelete={handleRemoveAttachment}
                  onClick={handleAttachmentClick}
                />
              ))}

              {editing && (
                <AddAttachment
                  height="100px"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icon icon="plus" size="1.6rem" color="gray7" />
                  <input
                    hidden
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleAddAttachment}
                  />
                </AddAttachment>
              )}
            </HandbookEntryAttachmentsContainer>
          </DropArea>
        )}
      </ContentWrapper>
    </Wrapper>
  )
}
